import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Stack, Tab, Tabs, Box } from '@mui/material';
import { CancelOutlined } from "@mui/icons-material";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import MainLayout from '../../layouts/MainLayout'
import CustomTabPanel from "../Common/CustomTabPanel";
import DeleteDialog from "../Common/DeleteDialog";
import TestPlanSuiteTable from "./TestPlanSuiteTable";
import BuildPlan from "./BuildPlan";
import EditDetail from "./EditDetail";
import TestPlanBuildsDrawer from "./TestPlanBuildsDrawer";
import TestPlanHeader from "./TestPlanHeader";
import TabsComponent from "../TestScenarios/TabsComponent";
import CICDIntegrations from "./CICDIntegrations.jsx";

import { detachPlanFromSuite } from '../../redux-store/testSuitePlanReducers/testSuitePlanActions';
import { getTestPlan } from "../../redux-store/testPlanReducers/testPlanActions";
import coreUtils from "../../utils/coreUtils";

import { getProject } from '../../redux-store/currentUserActions';
import { setSelectedProject } from 'reducers/commonSlice.js';
import PlanPrerequisiteScenarios from "./PlanPrerequisiteScenarios";

const drawerItems = [
  { icon: 'prerequisite', title: 'Prerequisite', id: 'prerequisite' },
  { icon: 'article', title: 'Details', id: 'detail' },
  { icon: 'play', title: 'Run', id: 'run' },
  // { icon: 'search', title: 'Debug', id: 'debug', disabled: true },
  { icon: 'build', title: 'Builds', id: 'builds' }
];

export function BasicDateTimePicker({ value, onChange }) {
  const styles = {
    dateTimePicker: {
      width: "200px",
      '& .MuiInputBase-root': {
        backgroundColor: 'var(--background-primary)',
        color: 'var(--text-primary)',
        '& fieldset': {
          borderColor: 'var(--borderColor)',
        },
        '&:hover fieldset': {
          borderColor: 'var(--primary-color-light)',
        },
        '&.Mui-focused fieldset': {
          borderColor: 'var(--primary-color)',
        },
      },
      '& .MuiInputLabel-root': {
        color: 'var(--text-secondary)',
      },
      '& .MuiInputBase-input': {
        color: 'var(--text-primary)',
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DemoContainer components={['DateTimePicker']}>
        <DateTimePicker
          sx={styles.dateTimePicker}
          label="Start Time"
          value={value}
          onChange={onChange}
        />
      </DemoContainer>
    </LocalizationProvider>
  );
}

const TestPlan = ({ project, testPlanId }) => {
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState(0);
  const [timeGap, setTimeGap] = useState({ days: 0, hours: 0, minutes: 0 });
  const [attachedSuites, setAttachedSuites] = useState([]);
  const [openDetachSuiteId, setOpenDetachSuiteId] = useState('');
  const [selectedItemContent, setSelectedItemContent] = useState(null);
  const testPlanDetail = useSelector(state => state.testPlan.selectedTestPlanDetail);
  const isLoading = useSelector((state) => state.testPlan.isLoading);
  const tabLabels = ["Test Suites", "CI/CD Integrations"];
  const [activeTab, setActiveTab] = useState(0);

  const styles = {
    mainLayout: {
      width: 'calc(100% - 100px)',
      backgroundColor: 'var(--background-primary)',
      color: 'var(--text-primary)',
    },
    leftSide: {
      maxWidth: "70%",
      backgroundColor: 'var(--background-primary)',
    },
    tabsContainer: {
      padding: "0px 24px",
      backgroundColor: 'var(--background-primary)',
    },
    customTab: {
      width: "max-content",
      padding: "16px 16px",
      color: 'var(--text-primary)',
      marginRight: "16px",
      '&.Mui-selected': {
        color: 'var(--primary-color)',
      },
    },
    contentBox: {
      maxHeight: "calc(100vh - 150px)",
      overflow: "auto",
      backgroundColor: 'var(--background-primary)',
    },
    dialogContent: {
      color: 'var(--text-primary)',
      marginBottom: '16px',
    },
  };

  useEffect(() => {
    fetchTestPlan();
  }, []);

  useEffect(() => {
    updateTestPlanState();
  }, [testPlanDetail]);

  const updateSelectedProjectInState = async (projectId) => {
    if(!projectId) return;
    const response = await dispatch(getProject({ projectId: projectId }));
    await dispatch(setSelectedProject(response.payload));
    localStorage.setItem('lastSelectedProject', JSON.stringify(response.payload));
  }

  const fetchTestPlan = async () => {
    const response = await dispatch(getTestPlan({ testPlanId }));
    await updateSelectedProjectInState(response?.payload?.project_id);
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const updateTestPlanState = () => {
    setAttachedSuites(testPlanDetail.test_suite_plans || []);
    if (testPlanDetail?.time_gap) setTimeGap(coreUtils.parseMinutes(testPlanDetail.time_gap));
  }

  const onDetachTestSuite = async () => {
    if (openDetachSuiteId) {
      await dispatch(detachPlanFromSuite({ testSuiteId: openDetachSuiteId, testPlanId }));
      setAttachedSuites([...attachedSuites.filter(planSuite => planSuite.test_suite.id !== openDetachSuiteId)]);
      setOpenDetachSuiteId('');
    }
  }

  const onDetachTestSuiteClick = (testSuiteId) => setOpenDetachSuiteId(testSuiteId);

  const renderDetachSuiteConfirmModal = () => {
    return (
      <DeleteDialog
        title="Confirm Detach"
        open={!!openDetachSuiteId}
        onClose={() => setOpenDetachSuiteId('')}
        deleteBtnLabel="Detach"
        secondaryBtnLabel="Cancel"
        secondaryBtnIcon={<CancelOutlined />}
        onDeleteAction={onDetachTestSuite}
        onSecondaryAction={() => setOpenDetachSuiteId('')}
      >
        <Typography variant="body1" sx={styles.dialogContent}>
          Are you sure you want to detach this Test Suite?
        </Typography>
      </DeleteDialog>
    )
  }

  const renderDrawerItem = ({ selectedItem, onDrawerClose }) => {
    const props = { projectId: project.id, testPlanId, testPlanDetail, attachedSuites, timeGap, setTimeGap, updateTestPlanState };
    switch (selectedItem) {
      case 'run':
        setSelectedItemContent(<BuildPlan {...props} closeDrawer={onDrawerClose} />);
        return false;
      // case 'debug':
      //   return <></>;
      case 'builds':
        setSelectedItemContent(<TestPlanBuildsDrawer {...props} closeDrawer={onDrawerClose} />);
        return false;
      case 'prerequisite':
        setSelectedItemContent(<PlanPrerequisiteScenarios {...props} closeDrawer={onDrawerClose} />);
        return false;
      default:
        setSelectedItemContent(<EditDetail {...props} closeDrawer={onDrawerClose} />);
        return false;
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return (<CICDIntegrations webhookBuildCurl={testPlanDetail?.webhook_curl} webhookBuildStatusCurl={testPlanDetail?.build_status_curl} />);
      default:
        return (<TestPlanSuiteTable attachedSuites={attachedSuites} onDetachTestSuiteClick={onDetachTestSuiteClick} isLoading={isLoading} />);
    }
  };

  return (
    <MainLayout
      sx={styles.mainLayout}
      headerText={testPlanDetail.title}
      isLoading={isLoading}
      drawerItems={drawerItems}
      renderDrawerItem={renderDrawerItem}
      enableSideDrawer={true}
      selectedItemContent={selectedItemContent}
      sxLeftSide={styles.leftSide}
      renderHeader={() => (<TestPlanHeader testPlanId={testPlanId} />)}
    >
      <TabsComponent
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        tabsStyle={styles.tabsContainer}
        tabLabels={tabLabels}
        customTabStyle={styles.customTab}
        activeTabStyle={{ color: "var(--primary-color)" }}
      />
      <Box sx={styles.contentBox}>
        <CustomTabPanel value={tabIndex} index={0}>
          {renderTabContent()}
        </CustomTabPanel>
      </Box>
      {renderDetachSuiteConfirmModal()}
    </MainLayout>
  );
};

export default TestPlan;
